import React, { useState, useEffect } from "react";
import { auth, db } from "./firebase";
import { signOut } from "firebase/auth";
import { useLanguage } from "./LanguageContext";
import Profile from "./Profile";
import Polls from "./Polls";
import Knowledge from "./Knowledge";
import Programs from "./Programs";
import {
  collection, getDocs, query, doc,
  updateDoc, onSnapshot
} from "firebase/firestore";

export default function Dashboard({ onAdmin }) {
  const { t, lang, setLang } = useLanguage();
  const [activeTab, setActiveTab] = useState("home");
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [announcements, setAnnouncements] = useState([]);

  const tabs = [
    { id: "home", icon: "home", label: t.home },
    { id: "knowledge", icon: "book", label: t.knowledge },
    { id: "polls", icon: "chart-pie", label: t.polls },
    { id: "programs", icon: "calendar", label: t.programs },
    { id: "profile", icon: "user", label: t.profile },
  ];

  useEffect(() => {
    const unsub = onSnapshot(doc(db, "users", auth.currentUser.uid), (snap) => {
      if (snap.exists()) setUserData(snap.data());
      else setUserData({});
    }, () => setUserData({}));
    fetchNotifs();
    fetchAnnouncements();
    return () => unsub();
  }, []);

  const isAdmin = auth.currentUser?.email === "honestpro77@gmail.com"
    || userData?.adminAccess === true;

  const fetchAnnouncements = async () => {
    try {
      const snap = await getDocs(query(collection(db, "announcements")));
      setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt));
    } catch {}
  };

  const fetchNotifs = async () => {
    try {
      const snap = await getDocs(query(collection(db, "notifications", auth.currentUser.uid, "items")));
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => b.createdAt - a.createdAt));
    } catch {}
  };

  const handleBellClick = async () => {
    const opening = !notifOpen;
    setNotifOpen(opening);
    if (opening && notifications.filter(n => n.unread).length > 0) {
      await Promise.all(notifications.filter(n => n.unread).map(n =>
        updateDoc(doc(db, "notifications", auth.currentUser.uid, "items", n.id), { unread: false })
      ));
      setNotifications(prev => prev.map(n => ({ ...n, unread: false })));
    }
  };

  const handleClearAll = async () => {
    await Promise.all(notifications.map(n =>
      updateDoc(doc(db, "notifications", auth.currentUser.uid, "items", n.id), { unread: false })
    ));
    setNotifications([]); setNotifOpen(false);
  };

  if (!userData) return (
    <div style={{ minHeight: "100vh", background: "#F7F7F9", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <p style={{ color: "#9CA3AF", fontSize: 14 }}>Loading...</p>
    </div>
  );

  const userNaam = userData.naam || "";
  const membershipStatus = userData.membershipStatus || "pending";
  const isActive = membershipStatus === "active";
  const unreadCount = notifications.filter(n => n.unread).length;
  const memberId = userData.membershipId || `GBS-${auth.currentUser.uid.slice(0, 8).toUpperCase()}`;
  const LANGS = ["en", "hi", "mr"];
  const latestAnnouncement = announcements[0];

  const QUICK_ACTIONS = [
    { icon: "id-badge", label: "ID Card", action: () => setActiveTab("profile") },
    { icon: "certificate", label: "Certificates", action: () => setActiveTab("profile") },
    { icon: "chart-pie", label: "Polls", action: () => setActiveTab("polls") },
    { icon: "calendar-event", label: "Programs", action: () => setActiveTab("programs") },
  ];

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.header}>
        <div style={S.headerTop}>
          <div>
            <p style={S.headerGreeting}>{t.hello}</p>
            <h2 style={S.headerName}>{userNaam} 🙏</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={S.langSwitcher}>
              {LANGS.map(l => (
                <button key={l} onClick={() => setLang(l)}
                  style={{ ...S.langBtn, background: lang === l ? "rgba(255,255,255,0.18)" : "transparent", fontWeight: lang === l ? 700 : 400 }}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            <div style={{ position: "relative" }}>
              <button onClick={handleBellClick} style={S.iconBtn}>
                <Icon name="bell" size={17} />
                {unreadCount > 0 && <span style={S.badge}>{unreadCount}</span>}
              </button>
              {notifOpen && (
                <div style={S.notifDropdown}>
                  <div style={S.notifHeader}>
                    <p style={S.notifTitle}>Notifications</p>
                    <button onClick={handleClearAll} style={S.clearBtn}>Clear All</button>
                  </div>
                  {notifications.length === 0 && <p style={S.noNotif}>No notifications</p>}
                  {notifications.map(n => (
                    <div key={n.id} style={S.notifItem}>
                      <p style={S.notifText}>{n.text}</p>
                      <p style={S.notifDate}>{new Date(n.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {isAdmin && <button onClick={onAdmin} style={S.iconBtn}><Icon name="shield" size={17} /></button>}
            <button onClick={() => signOut(auth)} style={S.iconBtn}><Icon name="logout" size={17} /></button>
          </div>
        </div>

        {/* Member info card */}
        <div style={S.memberCard}>
          <div>
            <p style={S.memberCardLabel}>Member ID</p>
            <p style={S.memberCardValue}>{memberId}</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={S.memberCardLabel}>Status</p>
            <p style={{ ...S.memberCardValue2 }}>{isActive ? t.active : "Pending"}</p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={S.memberCardLabel}>Role</p>
            <p style={S.memberCardValue2}>{(userData.role || "Member").replace(/_/g, " ")}</p>
          </div>
        </div>
      </div>

      <div style={S.content} className="gbs-fade-in">
        {activeTab === "home" && (
          <div>
            {/* Announcement */}
            {latestAnnouncement && (
              <div style={S.section}>
                <div style={S.sectionHeadRow}>
                  <p style={S.sectionLabel}>{t.announcements}</p>
                  <span style={S.sectionLink}>{announcements.length > 1 ? `+${announcements.length - 1} more` : ""}</span>
                </div>
                <div style={S.card}>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <div style={S.iconCircle}><Icon name="speakerphone" size={18} color="#fff" /></div>
                    <div>
                      <p style={S.cardTitle}>{latestAnnouncement.title}</p>
                      <p style={S.cardSubtitle}>{latestAnnouncement.message?.slice(0, 50)}{latestAnnouncement.message?.length > 50 ? "..." : ""}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div style={S.section}>
              <p style={S.sectionLabel}>Quick Actions</p>
              <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
                <div style={S.quickGrid}>
                  {QUICK_ACTIONS.map((qa, i) => (
                    <button key={qa.label} onClick={qa.action}
                      style={{
                        ...S.quickItem,
                        borderLeft: i % 2 === 1 ? "1px solid #ECECEE" : "none",
                        borderTop: i >= 2 ? "1px solid #ECECEE" : "none",
                      }}>
                      <div style={S.quickIconBox}><Icon name={qa.icon} size={17} color="#fff" /></div>
                      <span style={S.quickLabel}>{qa.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Future plans */}
            <div style={S.section}>
              <p style={S.sectionLabel}>{t.futurePlans}</p>
              <div style={S.card}>
                {[t.plan1, t.plan2, t.plan3].map((plan, i) => (
                  <div key={i} style={{ ...S.planItem, borderBottom: i < 2 ? "1px solid #ECECEE" : "none" }}>
                    <span style={S.planNum}>{i + 1}</span>
                    <span style={{ fontSize: 13, color: "#0D1B3E" }}>{plan}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === "programs" && <Programs />}
        {activeTab === "knowledge" && <Knowledge />}
        {activeTab === "polls" && <Polls />}
        {activeTab === "profile" && <Profile />}
      </div>

      {/* Bottom Nav */}
      <div style={S.bottomNav}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={S.navBtn}>
            <Icon name={tab.icon} size={21} color={activeTab === tab.id ? "#0D1B3E" : "#9CA3AF"} />
            <span style={{ fontSize: 10, fontWeight: activeTab === tab.id ? 700 : 500, color: activeTab === tab.id ? "#0D1B3E" : "#9CA3AF" }}>
              {tab.label}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Minimal inline icon set (stroke SVGs, no external deps) ──
function Icon({ name, size = 18, color = "#0D1B3E" }) {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: color, strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };
  switch (name) {
    case "home": return <svg {...common}><path d="M3 12l9-9 9 9" /><path d="M5 10v10h14V10" /></svg>;
    case "book": return <svg {...common}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>;
    case "chart-pie": return <svg {...common}><path d="M21.21 15.89A10 10 0 1 1 8 2.83" /><path d="M22 12A10 10 0 0 0 12 2v10z" /></svg>;
    case "calendar": return <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>;
    case "user": return <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21c0-4 4-6 8-6s8 2 8 6" /></svg>;
    case "bell": return <svg {...common}><path d="M6 8a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" /><path d="M10 21a2 2 0 0 0 4 0" /></svg>;
    case "shield": return <svg {...common}><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" /></svg>;
    case "logout": return <svg {...common}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>;
    case "speakerphone": return <svg {...common}><path d="M3 11v2a1 1 0 0 0 1 1h2l6 4V6L6 10H4a1 1 0 0 0-1 1z" /><path d="M17 8a4 4 0 0 1 0 8" /></svg>;
    case "id-badge": return <svg {...common}><rect x="5" y="3" width="14" height="18" rx="2" /><circle cx="12" cy="10" r="2" /><path d="M9 16h6" /></svg>;
    case "certificate": return <svg {...common}><circle cx="12" cy="8" r="6" /><path d="M9 14l-2 7 5-3 5 3-2-7" /></svg>;
    case "calendar-event": return <svg {...common}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><circle cx="12" cy="15" r="2" /></svg>;
    default: return null;
  }
}

const S = {
  container: { maxWidth: 420, margin: "0 auto", minHeight: "100vh", background: "#F7F7F9", fontFamily: "-apple-system, 'Segoe UI', sans-serif", position: "relative" },
  header: { background: "linear-gradient(135deg, #0D1B3E, #1A2F5A)", padding: "20px 20px 18px" },
  headerTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 16 },
  headerGreeting: { color: "rgba(255,255,255,0.45)", fontSize: 12, margin: 0, letterSpacing: 0.3 },
  headerName: { color: "#fff", margin: "2px 0 0", fontSize: 22, fontWeight: 600 },
  iconBtn: { background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 10, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", cursor: "pointer", position: "relative" },
  badge: { position: "absolute", top: -3, right: -3, background: "#fff", color: "#0D1B3E", borderRadius: "50%", width: 16, height: 16, fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" },
  langSwitcher: { display: "flex", gap: 1, background: "rgba(255,255,255,0.08)", borderRadius: 8, padding: 2 },
  langBtn: { border: "none", borderRadius: 6, padding: "5px 7px", fontSize: 10, color: "#fff", cursor: "pointer" },
  notifDropdown: { position: "absolute", right: 0, top: 42, width: 290, background: "#fff", borderRadius: 14, boxShadow: "0 8px 30px rgba(0,0,0,0.15)", zIndex: 200, maxHeight: 340, overflowY: "auto", border: "1px solid #ECECEE" },
  notifHeader: { padding: "12px 16px 8px", borderBottom: "1px solid #ECECEE", display: "flex", justifyContent: "space-between", alignItems: "center" },
  notifTitle: { margin: 0, fontSize: 13, fontWeight: 700, color: "#0D1B3E" },
  clearBtn: { background: "none", border: "none", fontSize: 11, color: "#9CA3AF", cursor: "pointer", fontWeight: 600 },
  noNotif: { padding: 16, fontSize: 13, color: "#9CA3AF", textAlign: "center" },
  notifItem: { padding: "12px 16px", borderBottom: "1px solid #F5F5F5" },
  notifText: { margin: 0, fontSize: 13, color: "#0D1B3E" },
  notifDate: { margin: "3px 0 0", fontSize: 11, color: "#9CA3AF" },
  memberCard: { background: "rgba(255,255,255,0.08)", borderRadius: 16, padding: "14px 16px", border: "1px solid rgba(255,255,255,0.12)", display: "flex", justifyContent: "space-between", alignItems: "center" },
  memberCardLabel: { margin: 0, color: "rgba(255,255,255,0.4)", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase" },
  memberCardValue: { margin: "4px 0 0", color: "#fff", fontSize: 17, fontWeight: 700, letterSpacing: 0.5 },
  memberCardValue2: { margin: "4px 0 0", color: "rgba(255,255,255,0.9)", fontSize: 13, fontWeight: 500, textTransform: "capitalize" },
  content: { padding: "20px 16px 90px" },
  section: { marginBottom: 14 },
  sectionHeadRow: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, padding: "0 4px" },
  sectionLabel: { margin: "0 0 10px 4px", fontSize: 12, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 },
  sectionLink: { fontSize: 11, color: "#9CA3AF" },
  card: { background: "#fff", borderRadius: 16, padding: 16, border: "1px solid #ECECEE" },
  iconCircle: { width: 38, height: 38, background: "#0D1B3E", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  cardTitle: { margin: 0, fontSize: 13, fontWeight: 600, color: "#0D1B3E" },
  cardSubtitle: { margin: "3px 0 0", fontSize: 12, color: "#9CA3AF" },
  quickGrid: { display: "grid", gridTemplateColumns: "1fr 1fr" },
  quickItem: { background: "none", border: "none", padding: "16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer", textAlign: "left" },
  quickIconBox: { width: 34, height: 34, background: "#0D1B3E", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  quickLabel: { fontSize: 13, fontWeight: 500, color: "#0D1B3E" },
  planItem: { display: "flex", alignItems: "center", gap: 10, padding: "10px 0" },
  planNum: { width: 22, height: 22, background: "#F1F1F4", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#0D1B3E", flexShrink: 0 },
  bottomNav: { position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 420, background: "#fff", borderTop: "1px solid #ECECEE", display: "flex", zIndex: 100 },
  navBtn: { flex: 1, padding: "10px 4px 16px", background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3 },
};